import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui';
import { ArrowLeftIcon, CheckCircleIcon, DocumentTextIcon } from '../../components/ui/Icons';
import { fetchTicketingDepartment } from './ticketingApi';
import { TICKETING_DATA_CHANGED_EVENT } from './ticketingStorage';
import {
  getTicketingFormComponent,
  getTicketingFormMeta,
  resolveCategoryFormKey,
} from './ticketingFormRegistry';
import { TicketingConversationModal } from './components/TicketingConversationModal';

const TicketingRequestPage = () => {
  const { departmentId, categoryId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [department, setDepartment] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [lastTicket, setLastTicket] = useState(null);
  const [conversationOpen, setConversationOpen] = useState(false);

  const load = useCallback(async () => {
    if (!departmentId || !categoryId) {
      setDepartment(null);
      setCategory(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const dep = await fetchTicketingDepartment(departmentId);
      setDepartment(dep);
      const idStr = String(categoryId);
      const cat = (dep.categories || []).find((c) => String(c.id) === idStr) || null;
      setCategory(cat);
    } catch {
      setDepartment(null);
      setCategory(null);
    } finally {
      setLoading(false);
    }
  }, [departmentId, categoryId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener(TICKETING_DATA_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(TICKETING_DATA_CHANGED_EVENT, refresh);
  }, [load]);

  const invalidCategoryDept =
    category && department && String(category.departmentId) !== String(department.id);

  const formKey = useMemo(() => resolveCategoryFormKey(category), [category]);
  const meta = useMemo(() => getTicketingFormMeta(formKey), [formKey]);
  const FormComponent = useMemo(() => getTicketingFormComponent(formKey), [formKey]);

  const deptPath = department ? `/ticketing/department/${department.id}` : '/ticketing/options';

  const resetForm = useCallback(() => {
    setSubmitted(false);
    setLastTicket(null);
    setConversationOpen(false);
  }, []);

  const handleSuccess = useCallback((ticket) => {
    setLastTicket(ticket);
    setSubmitted(true);
  }, []);

  if (loading) {
    return (
      <div className={cn('max-w-2xl mx-auto px-3 py-6 text-center', theme.text.muted)}>
        Loading…
      </div>
    );
  }

  if (!department || !category || invalidCategoryDept) {
    return (
      <div className={cn('max-w-2xl mx-auto px-3 py-6', theme.colors.pageBg)}>
        <div
          className={cn(
            'rounded-2xl border p-8 text-center',
            theme.border.light,
            theme.colors.surface
          )}
        >
          <p className={cn('font-medium', theme.text.primary)}>Request type not found</p>
          <p className={cn('text-sm mt-2', theme.text.muted)}>
            Check the link or set up departments and categories in ticketing setup.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>
              Go back
            </Button>
            <Link to="/ticketing/options">
              <Button variant="primary">Ticketing setup</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <>
        <div className={cn('max-w-2xl mx-auto px-3 py-6', theme.colors.pageBg)}>
          <div
            className={cn(
              'rounded-2xl border p-8 text-center',
              theme.border.light,
              theme.colors.surface,
              'border-green-200 dark:border-green-900/60'
            )}
          >
            <CheckCircleIcon className="h-14 w-14 mx-auto text-green-600 dark:text-green-400 mb-3" />
            <p className={cn('text-lg font-semibold', theme.text.primary)}>{meta.successMessage}</p>
            <p className={cn('text-sm mt-2', theme.text.muted)}>
              Your request was saved. Ticket ID: #{lastTicket?.id}
              <br />
              <span className="text-xs opacity-90">
                You can read and send messages on this ticket anytime.
              </span>
              {lastTicket?.ticketDate ? (
                <>
                  <br />
                  Ticket date:{' '}
                  {new Date(lastTicket.ticketDate).toLocaleDateString(undefined, {
                    dateStyle: 'medium',
                  })}
                </>
              ) : null}
              {lastTicket?.ticketRequestedFromUser ? (
                <>
                  <br />
                  Requested by:{' '}
                  {lastTicket.ticketRequestedFromUser.userDisplayName ||
                    lastTicket.ticketRequestedFromUser.userEmail ||
                    `#${lastTicket.ticketRequestedFromUser.id}`}
                </>
              ) : null}
              {lastTicket?.ticketOpenedStatusDate ? (
                <>
                  <br />
                  Opened:{' '}
                  {new Date(lastTicket.ticketOpenedStatusDate).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </>
              ) : null}
              {lastTicket?.ticketInProgressStatusDate ? (
                <>
                  <br />
                  In progress:{' '}
                  {new Date(lastTicket.ticketInProgressStatusDate).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </>
              ) : null}
              {lastTicket?.ticketResolvedStatusDate ? (
                <>
                  <br />
                  Resolved:{' '}
                  {new Date(lastTicket.ticketResolvedStatusDate).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </>
              ) : null}
              {lastTicket?.ticketCanceledStatusDate ? (
                <>
                  <br />
                  Canceled:{' '}
                  {new Date(lastTicket.ticketCanceledStatusDate).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </>
              ) : null}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button variant="primary" type="button" onClick={resetForm}>
                New request (same type)
              </Button>
              {lastTicket?.id != null ? (
                <Button variant="secondary" type="button" onClick={() => setConversationOpen(true)}>
                  View comments
                </Button>
              ) : null}
              <Link to={deptPath}>
                <Button variant="secondary" type="button">
                  All request types
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <TicketingConversationModal
          isOpen={conversationOpen && lastTicket?.id != null}
          onClose={() => setConversationOpen(false)}
          ticket={
            lastTicket?.id != null
              ? {
                  id: lastTicket.id,
                  ticketRequestedFromUserId: lastTicket.ticketRequestedFromUserId,
                }
              : null
          }
        />
      </>
    );
  }

  return (
    <div className={cn('max-w-3xl mx-auto px-3 py-5 pb-10', theme.colors.pageBg)}>
      <div
        className={cn(
          'mb-8 rounded-2xl border p-4 md:p-5',
          theme.border.light,
          theme.colors.surface,
          theme.shadow.sm
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <Link
            to={deptPath}
            className={cn(
              'inline-flex items-center gap-2 text-sm font-medium',
              'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
            )}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to {department.name}
          </Link>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-300">
              Department: {department.name}
            </span>
            <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-2.5 py-1 text-xs font-semibold">
              {category.name}
            </span>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div
            className={cn(
              'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl',
              'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
              'ring-1 ring-blue-100 dark:ring-blue-900/50'
            )}
          >
            <DocumentTextIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className={cn('text-2xl font-bold tracking-tight', theme.text.primary)}>
              {meta.requestHeading}
            </h1>
            <p className={cn('text-sm mt-1', theme.text.muted)}>
              Complete the form below to submit your request to{' '}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {department.name}
              </span>
              .
            </p>
          </div>
        </div>
      </div>

      <FormComponent
        key={`${category.id}-${formKey}`}
        department={department}
        category={category}
        deptPath={deptPath}
        navigate={navigate}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default TicketingRequestPage;
